import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "@phosphor-icons/react";

const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Service",
    lastUpdated: "April 2026",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: "By accessing and using Quibn (\"the Service\"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service."
      },
      {
        heading: "2. Description of Service",
        body: "Quibn is an AI-powered content generation tool designed for coaches, consultants, and service-based professionals. The Service uses third-party language models (via OpenRouter) to generate marketing content based on your inputs and brand voice settings."
      },
      {
        heading: "3. User Accounts",
        body: "You must sign in via Google to access the Service. You are responsible for maintaining the security of your account and all activity under it. You must be at least 18 years old to use this Service."
      },
      {
        heading: "4. Acceptable Use",
        body: "You agree not to use the Service to generate content that is unlawful, defamatory, harassing, or infringes on intellectual property rights. We reserve the right to suspend accounts that violate these guidelines."
      },
      {
        heading: "5. AI-Generated Content",
        body: "Content produced by the Service is generated by AI and may contain inaccuracies. You are solely responsible for reviewing, editing, and publishing any generated content. We make no guarantees regarding the accuracy, completeness, or suitability of AI outputs."
      },
      {
        heading: "6. API Keys & Third-Party Services",
        body: "Certain features require you to provide your own API key (e.g., OpenRouter). You are responsible for any costs incurred through your API key usage. We do not store or share your API keys beyond what is necessary to provide the Service."
      },
      {
        heading: "7. Subscriptions & Credit Packs",
        body: "Quibn offers monthly subscription plans with tiered usage limits, and optional one-time credit pack purchases. Subscriptions renew automatically each billing cycle. Credit packs are governed by the Credit Pack Policy at /legal/credits. By purchasing a credit pack, you agree to the terms outlined in that policy."
      },
      {
        heading: "8. Intellectual Property",
        body: "You retain ownership of the inputs you provide and the content you generate using the Service. We retain ownership of the Service, its design, and underlying technology."
      },
      {
        heading: "9. Limitation of Liability",
        body: "The Service is provided \"as is\" without warranties of any kind. We shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service."
      },
      {
        heading: "10. Changes to Terms",
        body: "We may update these terms from time to time. Continued use of the Service after changes constitutes acceptance of the revised terms."
      },
      {
        heading: "11. Contact",
        body: "For questions about these terms, please reach out via the contact information on our landing page."
      }
    ]
  },
  privacy: {
    title: "Privacy Policy",
    lastUpdated: "April 2026",
    sections: [
      {
        heading: "1. Information We Collect",
        body: "When you sign in with Google, we receive your name, email address, and profile picture. As you use Quibn, we store the data you enter: brand voice settings, content inputs, generated content, saved templates, resource bank files, and team membership details. We also collect basic usage data (which features you use, when you log in) to improve the Service."
      },
      {
        heading: "2. How We Use Your Information",
        body: "In plain terms: we use your data to make Quibn work for you. Specifically: your brand voice and profile data are injected into AI prompts so your content sounds like you. Your content history is stored so you can access, edit, and reuse it. Usage data helps us understand which features matter most and where to improve. We never sell your data to advertisers or third parties."
      },
      {
        heading: "3. Who Sees Your Data",
        body: "Your content, brand profiles, and files are private to your account. Team members you invite can access shared workspace content based on their role. Our admin team (Quibn staff) can access account data for support and debugging purposes only. AI model providers (via OpenRouter) process your content inputs to generate output — they receive only the prompt text, not your account details."
      },
      {
        heading: "4. How Long We Keep Your Data",
        body: "Your data stays in your account as long as it is active. When you delete content, it goes to a 30-day trash bin — after that, it is permanently erased. If you delete your account, all your data is permanently removed after a 72-hour grace period. We do not keep shadow copies. Anonymized, aggregated statistics (like 'total generations this month across all users') may be retained indefinitely but can never be traced back to you."
      },
      {
        heading: "5. Third-Party Services We Use",
        body: "Google Authentication (sign-in only — we don't access your Gmail, Drive, or other Google data). OpenRouter (sends your content prompts to AI models like GPT, Claude, Gemini for generation). Stripe (processes payments — we never see or store your full card number). Cloud hosting (MongoDB Atlas for database, standard infrastructure providers). None of these services receive more data than what is strictly necessary for their function."
      },
      {
        heading: "6. API Key Handling",
        body: "If you provide an OpenRouter API key (required for Agency/Enterprise tiers), it is stored encrypted in our database and used solely for generating content on your behalf. We never share, log, or expose your API key to any party other than OpenRouter during generation requests."
      },
      {
        heading: "7. Cookies",
        body: "We use one essential session cookie to keep you signed in. That's it. No tracking cookies, no advertising pixels, no analytics cookies from third parties. The session cookie is httponly (JavaScript cannot read it) and secure (only sent over HTTPS)."
      },
      {
        heading: "8. Data Security",
        body: "We use HTTPS everywhere, encrypted database connections, and secure session management. API keys are stored encrypted. Access to production systems is restricted to authorized personnel. While no system is perfectly secure, we follow industry-standard practices to protect your data."
      },
      {
        heading: "9. Your Rights",
        body: "You can: view all your data in Settings, export everything as JSON ('Download My Data' in Settings > Workspace), delete individual items (they go to trash for 30 days, then are permanently removed), and delete your entire account at any time (permanently erased after 72-hour grace period). If you are in the EU/EEA, you also have rights under GDPR including the right to access, rectification, erasure, and data portability — all of which are built into the product."
      },
      {
        heading: "10. Children's Privacy",
        body: "Quibn is designed for professionals and is not intended for users under the age of 18. We do not knowingly collect data from minors."
      },
      {
        heading: "11. Changes to This Policy",
        body: "If we make significant changes to how we handle your data, we will notify you through the Service (in-app notification or email). Minor wording updates will be reflected in the 'Last updated' date. Your continued use after changes means you accept the revised policy."
      },
      {
        heading: "12. Contact",
        body: "For privacy questions or to exercise your data rights, use the in-app feedback button or email us at the address on our landing page. We aim to respond within 5 business days."
      }
    ]
  },
  credits: {
    title: "Credit Pack Policy",
    lastUpdated: "April 2026",
    sections: [
      {
        heading: "1. What Are Credit Packs?",
        body: "Credit packs are one-time purchases of additional content generation credits for your Quibn account. They supplement your monthly plan allowance and are available to users on Free Beta (25 generations/month) and Creator (500 generations/month) tiers."
      },
      {
        heading: "2. How Credits Are Consumed",
        body: "Your monthly plan allowance is always consumed first. Purchased credits are only used after your monthly allowance is fully exhausted. When purchased credits are consumed, the oldest credits are used first (first in, first out). This means the credits closest to expiration are used before newer ones."
      },
      {
        heading: "3. Credit Expiration",
        body: "Purchased credits expire six (6) months from the date of purchase. Unused credits are forfeited upon expiration. Credits are not prorated or partially refundable. Your monthly plan allowance resets each billing cycle regardless of purchased credit balances."
      },
      {
        heading: "4. Rollover",
        body: "Purchased credits roll over from month to month within their six-month validity period. Your monthly plan allowance does not roll over — it resets to zero at the start of each billing cycle."
      },
      {
        heading: "5. Purchase Limits",
        body: "You may purchase a maximum of two (2) credit packs per calendar month. This limit applies regardless of pack size. If you consistently need more generations, we recommend upgrading to a higher-tier plan."
      },
      {
        heading: "6. Available Packs",
        body: "Credit packs are available in the following sizes: 100 credits ($20), 250 credits ($50), 500 credits ($200), and 1,000 credits ($500). Prices are in USD. Pricing is subject to change with reasonable notice."
      },
      {
        heading: "7. Refunds",
        body: "Credit pack purchases are non-refundable. All sales are final. If you experience a technical issue preventing credit use, contact support for assistance."
      },
      {
        heading: "8. Account Changes",
        body: "If you upgrade your subscription plan, your purchased credits remain in your account and continue to follow the same expiration rules. If your account is downgraded (e.g., subscription cancellation), purchased credits remain available until they expire. If your account is terminated for violation of our Terms of Service, all credits are forfeited."
      },
      {
        heading: "9. Changes to This Policy",
        body: "We may update this Credit Pack Policy from time to time. Changes will not retroactively affect credits already purchased. Continued purchase of credit packs after changes constitutes acceptance of the revised policy."
      }
    ]
  },
  billing: {
    title: "Billing & Refund Policy",
    lastUpdated: "April 2026",
    sections: [
      {
        heading: "1. Subscription Plans",
        body: "Quibn offers monthly subscription plans (Creator at $49/mo, Studio at $149/mo, Agency at $299/mo) and a free tier (Free Beta). All paid subscriptions renew automatically at the start of each billing cycle unless canceled before the renewal date."
      },
      {
        heading: "2. How Billing Works",
        body: "When you upgrade to a paid plan, you are charged immediately for the first billing cycle. Subsequent charges occur on the same date each month. All prices are in USD. Applicable taxes may be added based on your location."
      },
      {
        heading: "3. Cancellations",
        body: "You can cancel your subscription at any time from your Stripe billing portal. When you cancel, your paid features remain active until the end of your current billing cycle. After that, your account reverts to the Free Beta tier. You will not be charged again after cancellation."
      },
      {
        heading: "4. Refunds",
        body: "Subscription charges are generally non-refundable. However, if you were charged in error, experienced a technical issue that prevented you from using the Service during your billing period, or believe a charge was unauthorized, contact us within 14 days of the charge for review. We handle refund requests on a case-by-case basis."
      },
      {
        heading: "5. Credit Packs",
        body: "Credit pack purchases are one-time, non-refundable transactions. Credits expire 6 months after purchase. For full details, see our Credit Pack Policy at /legal/credits."
      },
      {
        heading: "6. Storage Add-ons",
        body: "Storage add-ons are monthly subscriptions that expand your resource bank capacity. They follow the same billing cycle as your main subscription. If canceled, the extra storage slots are removed at the end of the billing period. Files already uploaded remain accessible, but you cannot add new files above your base limit."
      },
      {
        heading: "7. Plan Changes",
        body: "Upgrading your plan takes effect immediately — you get access to higher-tier features right away and are charged the new rate. Downgrading takes effect at the end of your current billing cycle. No partial refunds are issued for downgrades."
      },
      {
        heading: "8. Failed Payments",
        body: "If a payment fails, we will attempt to charge your card again. After repeated failures, your account enters a 7-day grace period during which your features remain active. If payment is not resolved within the grace period, your account is automatically downgraded to Free Beta."
      },
      {
        heading: "9. Dispute Resolution",
        body: "If you have a billing question or dispute, please contact us before filing a chargeback with your bank. We are committed to resolving billing issues quickly and fairly. Unresolved chargebacks may result in account suspension."
      },
      {
        heading: "10. Changes to Pricing",
        body: "We may adjust pricing with at least 30 days' notice. Price changes apply to new billing cycles only — your current cycle is never affected retroactively. If you do not agree with a price change, you can cancel before your next renewal."
      }
    ]
  },
  "data-retention": {
    title: "Data Retention & Deletion Policy",
    lastUpdated: "April 2026",
    sections: [
      {
        heading: "1. Trash Bin",
        body: "When you delete content, resource bank files, or uploaded files, they are moved to a 30-day trash bin. During this period, you can restore items at any time from Settings > Trash. After 30 days, trashed items are permanently and irreversibly deleted from our systems. The trash bin does not count toward your resource bank storage limits."
      },
      {
        heading: "2. Account Deletion",
        body: "You can delete your account at any time from Settings > Workspace > Delete My Account. Account deletion is self-service and requires email confirmation. Once initiated, there is a 72-hour grace period during which you can cancel the deletion. After 72 hours, all account data is permanently and irreversibly deleted. This includes: all generated content, brand voice profiles, resource bank files, uploaded files, templates, usage history, API keys, payment records, and team memberships. There is no way to recover any data after the 72-hour grace period."
      },
      {
        heading: "3. Stripe Subscriptions",
        body: "When you delete your account, any active Stripe subscription is immediately canceled. You will not be charged again. If you have a storage add-on subscription, it is also canceled immediately."
      },
      {
        heading: "4. What We Delete",
        body: "Upon account deletion (after 72 hours), we permanently remove: your user profile and authentication records, all account and workspace data, all content generations and drafts, all resource bank entries and uploaded files, all templates and brand voice settings, all usage logs and API keys, all payment transaction records, all team memberships and invitations, and all audit log entries associated with your account."
      },
      {
        heading: "5. What We Retain",
        body: "We may retain anonymized, aggregated usage statistics (e.g., total platform generations) that cannot be linked back to your account. We may also retain records required by law (e.g., financial transaction summaries for tax compliance) for the legally mandated period."
      },
      {
        heading: "6. Expired Credit Packs",
        body: "Credit packs that have been fully consumed and have passed their expiration date are automatically removed from our systems during routine maintenance."
      },
      {
        heading: "7. Session Cleanup",
        body: "Expired authentication sessions are automatically cleaned up during routine maintenance. This is a background process and does not require any action from you."
      },
      {
        heading: "8. Changes to This Policy",
        body: "We may update this Data Retention & Deletion Policy from time to time. Changes will be reflected in the 'Last updated' date above. Continued use of the Service after changes constitutes acceptance of the revised policy."
      }
    ]
  }
};

export default function LegalPage() {
  const { docType } = useParams();
  const content = LEGAL_CONTENT[docType];

  if (!content) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center p-6" data-testid="legal-page-not-found">
        <div className="text-center">
          <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)] mb-4">Page Not Found</h1>
          <Link to="/login" className="text-[#FF4400] hover:underline">Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="legal-page">
      <div className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors mb-8"
            data-testid="legal-back-link"
          >
            <ArrowLeft size={16} />
            Back to login
          </Link>

          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[var(--text-primary)] tracking-tight mb-2" data-testid="legal-page-title">
            {content.title}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-10">
            Last updated: {content.lastUpdated}
          </p>

          <div className="space-y-8">
            {content.sections.map((section, i) => (
              <section key={`legal-${section.heading.replace(/\s+/g, '-')}`}>
                <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)] mb-2">
                  {section.heading}
                </h2>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                  {section.body}
                </p>
              </section>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-[var(--border-default)]">
            <p className="text-xs text-[var(--text-muted)]">
              Quibn &mdash; {content.title}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
